import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { supabaseStorageCompat as supabase } from "./fileStorage";
import { getSession, saveSession, clearSession, getCompany, saveCompany, fetchCompanyFromDatabase, fetchUserFromDatabase, getTeam, saveTeam, getUsers, logoutUser, userFromSupabase } from "./Auth";
import TodoPage from "./Todopage";

// Pages
import AuthScreen        from "./Authscreen";
import AcceptInvite      from "./AcceptInvite";
import CaseDetail        from "./CaseDetail";
import CryptoAnalysisPage from "./CryptoAnalysisPage";
import KPIGraphGenerator from "./KPIGraphGenerator";
import KYCOnboardingPortal, { KYCReviewDashboard } from "./KYCOnboardingPortal";
import PoliciesPage      from "./PoliciesPage";
import SanctionsScreeningPage from "./KYCPage";
import TrainingPage      from "./Trainingpage";
import EDDPage           from "./EDDPage";
import TransactionMonitoring from "./Transactionmonitoring";
import SARPage           from "./SARPage";
import TravelRulePage    from "./TravelRulePage";
import CustomersPage     from "./CustomersPage";
import ComplianceProgramBuilder from "./ComplianceProgramBuilder";
import CTRPage from "./CTRPage";
import WatchlistPage from "./WatchlistPage";
import NetworkGraphPage from "./NetworkGraphPage";
import TeamChat from "./TeamChat";
import AuditPage from "./AuditPage";
import RegulatoryReportingPage from "./RegulatoryReportingPage";
import ComplianceCalendarPage   from "./ComplianceCalendarPage";
import RegulatoryCorrespondencePage from "./RegulatoryCorrespondencePage";
import IncidentResponsePage     from "./IncidentResponsePage";
import ThirdPartyDDPageEnhanced  from "./ThirdPartyDDPageEnhanced";
import DDQuestionnaireClient    from "./DDQuestionnaireClient";
import ExceptionManagementPage   from "./ExceptionManagementPage";
import EffectivenessTestingPage   from "./EffectivenessTestingPage";
import AuditDocumentation        from "./AuditDocumentation";

// Extracted components
import AIAssistant       from "./AIAssistant";
import CaseManagement    from "./CaseManagement";
import Dashboard         from "./Dashboard";
import SettingsPage      from "./SettingsPage";
import TeamPage          from "./TeamPage";
import ErrorBoundary     from "./ErrorBoundary";
import NotificationBell  from "./NotificationBell";
import GlobalSearch      from "./GlobalSearch";

import { C, LIGHT, ThemeContext } from "./theme";
import { useAuditLog } from "./useAuditLog";

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
function Sidebar({ active, setActive, company, currentUser, onLogout, openAlerts, lightMode, setLightMode, isMobile }) {
  const [hoveredLocked, setHoveredLocked] = useState(null);
  const T = lightMode ? LIGHT : C;
  const { t, i18n } = useTranslation();

  const LOCKED_ROLES = ["admin", "compliance_officer", "supervisor"];
  const ANALYST_ACCESSIBLE = ["admin", "analyst", "compliance_officer", "supervisor"];
  const items = [
    // ── Operations ──────────────────────────────────────────────────────────
    { separator: true, label: t('sidebar.headers.operations') },
    { id: "dashboard",  label: t('sidebar.nav.dashboard'),  icon: "⬡" },
    { id: "cases",      label: t('sidebar.nav.cases'),      icon: "◈", badge: openAlerts },
    { id: "tm",         label: t('sidebar.nav.tm'),         icon: "⇄" },
    { id: "sar",        label: t('sidebar.nav.sar'),        icon: "◉" },
    { id: "ctr",        label: t('sidebar.nav.ctr'),        icon: "💵" },
    // ── Customers ───────────────────────────────────────────────────────────
    { separator: true, label: t('sidebar.headers.customers') },
    { id: "customers",  label: t('sidebar.nav.customers'),  icon: "👥" },
    { id: "onboarding", label: t('sidebar.nav.onboarding'), icon: "✦" },
    { id: "kyc",        label: t('sidebar.nav.kyc'),        icon: "🪪" },
    { id: "edd",        label: t('sidebar.nav.edd'),        icon: "◈" },
    { id: "travelrule", label: t('sidebar.nav.travelrule'), icon: "⇌" },
    // ── Investigations ──────────────────────────────────────────────────────
    { separator: true, label: t('sidebar.headers.investigations') },
    { id: "watchlist",  label: t('sidebar.nav.watchlist'),  icon: "🚫" },
    { id: "network",    label: t('sidebar.nav.network'),    icon: "◎" },
    { id: "crypto",     label: t('sidebar.nav.crypto'),     icon: "₿" },
    { id: "thirdpartydd", label: t('sidebar.nav.thirdpartydd'), icon: "🤝", roles: LOCKED_ROLES },
    // ── Compliance ──────────────────────────────────────────────────────────
    { separator: true, label: t('sidebar.headers.compliance') },
    { id: "compliance", label: t('sidebar.nav.compliance'),  icon: "🏛" },
    { id: "policies",   label: t('sidebar.nav.policies'),   icon: "◉" },
    { id: "calendar",   label: t('sidebar.nav.calendar'),   icon: "📅", trialLocked: true, roles: LOCKED_ROLES },
    { id: "regreporting", label: t('sidebar.nav.regreporting'), icon: "📑", trialLocked: true, roles: LOCKED_ROLES },
    { id: "regcorrespondence", label: t('sidebar.nav.regcorrespondence'), icon: "📨", trialLocked: true, roles: LOCKED_ROLES },
    { id: "auditdocs",  label: t('sidebar.nav.auditdocs'),  icon: "📋", trialLocked: true, roles: LOCKED_ROLES },
    { id: "incident",   label: t('sidebar.nav.incident'),   icon: "⚠",  trialLocked: true, roles: LOCKED_ROLES },
    { id: "training",   label: t('sidebar.nav.training'),   icon: "◭", trialLocked: true },
    { id: "exceptions", label: t('sidebar.nav.exceptions'), icon: "⚡", trialLocked: true, roles: LOCKED_ROLES },
    { id: "effectivenesstesting", label: t('sidebar.nav.testing'), icon: "🔎", trialLocked: true, roles: LOCKED_ROLES },
    // ── Tools ───────────────────────────────────────────────────────────────
    { separator: true, label: t('sidebar.headers.tools') },
    { id: "ai",         label: t('sidebar.nav.ai'),         icon: "✦" },
    { id: "kpi",        label: t('sidebar.nav.kpi'),        icon: "📊", trialLocked: true, roles: LOCKED_ROLES },
    { id: "todo",       label: t('sidebar.nav.todo'),       icon: "✓" },
    { id: "chat",       label: t('sidebar.nav.chat'),       icon: "💬" },
    // ── Admin ────────────────────────────────────────────────────────────────
    { separator: true, label: t('sidebar.headers.admin') },
    { id: "audit",      label: t('sidebar.nav.audit'),      icon: "◬", roles: LOCKED_ROLES },
    { id: "team",       label: t('sidebar.nav.team'),       icon: "◍", roles: ["admin"] },
    { id: "settings",   label: t('sidebar.nav.settings'),   icon: "◎", roles: ANALYST_ACCESSIBLE },
  ];

  if (!currentUser) return null;
  const initials = `${currentUser.firstName?.[0] || ""}${currentUser.lastName?.[0] || ""}`.toUpperCase();

  return (
    <div style={{ width: isMobile ? "100%" : 224, maxWidth: isMobile ? "100vw" : "auto", minHeight: isMobile ? "auto" : "100vh",
      background: T.surface,
      borderRight: isMobile ? "none" : `1px solid ${T.border}`, display: "flex",
      flexDirection: "column", flexShrink: 0 }}>

      {/* Logo + company */}
      <div style={{ padding: "22px 16px 14px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ width: 30, height: 30, background: T.accent,
            clipPath: "polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)",
            flexShrink: 0 }} />
          <div>
            <div style={{ color: T.text, fontFamily: "'Syne',sans-serif",
              fontWeight: 700, fontSize: 14 }}>CrossRegGuard</div>
            <div style={{ color: T.textDim, fontSize: 9, letterSpacing: "0.12em" }}>{t('sidebar.complianceOs')}</div>
          </div>
        </div>

        {/* Company pill + notification bell in same row */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ flex: 1, padding: "5px 10px", background: T.accentGlow,
            border: `1px solid ${T.border}`, borderRadius: 6,
            display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%",
              background: T.success, flexShrink: 0 }} />
            <div style={{ color: T.textMid, fontSize: 11, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {company?.name || t('sidebar.yourCompany')}
            </div>
          </div>
          <NotificationBell currentUser={currentUser} company={company} onNavigate={setActive} />
        </div>

        {/* Global search */}
        <div style={{ marginTop: 10 }}>
          <GlobalSearch company={company} onNavigate={setActive} />
        </div>
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: "12px 8px", overflowY: "auto" }}>
        {items.map((item, idx) => {
          if (item.separator) return (
            <div key={`sep-${idx}`} style={{
              padding: "10px 10px 4px", color: T.textDim,
              fontSize: 9, letterSpacing: "0.14em", fontFamily: "'DM Mono',monospace",
              borderTop: idx > 0 ? `1px solid ${T.border}` : "none", marginTop: idx > 0 ? 6 : 0,
            }}>{item.label}</div>
          );
          const isRoleLocked = item.roles && !item.roles.includes(currentUser?.permission);
          const isLocked = (item.trialLocked && currentUser?.permission !== "admin") || isRoleLocked;
          return (
            <div key={item.id} style={{ position: "relative" }}
              onMouseEnter={() => isLocked && setHoveredLocked(item.id)}
              onMouseLeave={() => setHoveredLocked(null)}>
              <button
                onClick={() => !isLocked && setActive(item.id)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px", borderRadius: 7, marginBottom: 2,
                  background: active === item.id ? T.accentGlow : "transparent",
                  border: active === item.id ? `1px solid ${T.borderLight}` : "1px solid transparent",
                  color: isLocked ? T.textDim : active === item.id ? T.accent : T.textMid,
                  cursor: isLocked ? "not-allowed" : "pointer", textAlign: "left",
                  transition: "all 0.12s", fontFamily: "'DM Mono',monospace",
                  fontSize: 12, opacity: isLocked ? 0.5 : 1 }}
              >
                <span style={{ fontSize: 14 }}>{item.icon}</span>
                {item.label}
                {isLocked && <span style={{ marginLeft: "auto", fontSize: 10 }}>🔒</span>}
                {!isLocked && item.badge > 0 && (
                  <span style={{ marginLeft: "auto", background: T.danger,
                    color: "#fff", fontSize: 10, padding: "2px 6px",
                    borderRadius: 10, fontWeight: 700 }}>
                    {item.badge}
                  </span>
                )}
              </button>
              {isLocked && hoveredLocked === item.id && (
                <div style={{ position: "absolute", left: "105%", top: "50%",
                  transform: "translateY(-50%)", background: C.surface,
                  border: `1px solid ${C.border}`, borderRadius: 7,
                  padding: "7px 11px", whiteSpace: "nowrap", color: C.text,
                  fontSize: 11, fontFamily: "'DM Mono',monospace", zIndex: 999,
                  pointerEvents: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
                  {t('common.contactAdmin')}
                  <div style={{ position: "absolute", right: "100%", top: "50%",
                    transform: "translateY(-50%)", borderWidth: 5, borderStyle: "solid",
                    borderColor: `transparent ${C.surface} transparent transparent` }} />
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%",
            background: `linear-gradient(135deg,${T.accent},${T.accentDim})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: T.bg, fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
            {initials}
          </div>
          <div style={{ overflow: "hidden", flex: 1 }}>
            <div style={{ color: T.text, fontSize: 12, fontWeight: 600,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {currentUser.firstName} {currentUser.lastName}
            </div>
            <div style={{ color: T.accent, fontSize: 10, marginTop: 1 }}>{currentUser.role}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={onLogout}
            style={{ flex: 1, padding: "6px", background: "transparent",
              border: `1px solid ${T.border}`, borderRadius: 6, color: T.textDim,
              cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
            {t('common.signOut')}
          </button>
          {['en', 'pt', 'es'].map(lang => (
            <button key={lang}
              onClick={() => i18n.changeLanguage(lang)}
              title={lang === 'en' ? t('common.switchToEn') : lang === 'pt' ? t('common.switchToPt') : t('common.switchToEs')}
              style={{ padding: "6px 8px", background: i18n.language === lang ? T.accentGlow : "transparent",
                border: `1px solid ${i18n.language === lang ? T.accent : T.border}`, borderRadius: 6,
                color: i18n.language === lang ? T.accent : T.textDim,
                cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
              {lang === 'en' ? '🇺🇸 EN' : lang === 'pt' ? '🇧🇷 PT' : '🇪🇸 ES'}
            </button>
          ))}
          <button
            onClick={() => setLightMode(m => {
              const next = !m;
              localStorage.setItem("crossregguard_theme", next ? "light" : "dark");
              return next;
            })}
            title={lightMode ? t('common.switchDark') : t('common.switchLight')}
            style={{ padding: "6px 8px", background: "transparent",
              border: `1px solid ${T.border}`, borderRadius: 6, color: T.textDim,
              cursor: "pointer", fontSize: 12 }}>
            {lightMode ? "🌙" : "☀"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
let companyCreationInProgress = false;
let isLoggingOut    = false;
let isAuthenticating = false;

export default function App() {
  const { t } = useTranslation();

  // ── Public Questionnaire Route (no auth required) ────────────────────────
  const ddqToken = new URLSearchParams(window.location.search).get('ddq');
  if (ddqToken) {
    return <DDQuestionnaireClient tokenParam={ddqToken} />;
  }

  const [phase, setPhase]             = useState("loading");
  const [company, setCompanyState]    = useState(() => getCompany());
  const [currentUser, setCurrentUser] = useState(null);
  const [team, setTeamState]          = useState(() => getTeam());
  const [active, setActive]           = useState("dashboard");
  const [alerts, setAlerts]           = useState([]);
  const [tmAlerts, setTmAlerts]       = useState([]);
  const [openCase, setOpenCase]       = useState(null);
  const [cryptoSearchData, setCryptoSearchData] = useState(null);
  const [agingFilter, setAgingFilter] = useState(null);
  const [selectedDmPerson, setSelectedDmPerson] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [customSLAThresholds, setCustomSLAThresholds] = useState({
    CRITICAL: 3,
    HIGH: 7,
    MEDIUM: 14,
    LOW: 21,
  });
  const { log: auditLog } = useAuditLog(
    company ?? null,
    currentUser ?? null
  );
  const [lightMode, setLightMode]     = useState(
    () => localStorage.getItem("crossregguard_theme") === "light"
  );

  const T = lightMode ? LIGHT : C; // active theme

  // ── Redirect root path to home page ──────────────────────────────────────────
  useEffect(() => {
    // Check if visiting root path (/) and redirect to home page
    if (window.location.pathname === '/' && !currentUser) {
      // Only redirect unauthenticated users at root to the home page
      window.location.href = '/crossregnexus.html';
    }
  }, [currentUser]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [active]);

  function handleNavigate(page, options = null) {
    if (options) {
      if (options.filter) setAgingFilter(options.filter);
      if (options.dmPerson) setSelectedDmPerson(options.dmPerson);
    }
    setActive(page);
  }

  // ── Load cases from Neon database ──────────────────────
  async function loadCases(companyId) {
    try {
      const token = localStorage.getItem('amlguard_token');
      console.log('[App] loadCases called with companyId:', companyId, 'token:', !!token);

      if (token && companyId) {
        // Try loading from Neon first
        const url = `/api/query?resource=cases&company_id=${companyId}`;
        console.log('[App] Fetching cases from:', url);

        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        console.log('[App] Cases API response status:', res.status);

        if (res.ok) {
          const data = await res.json();
          console.log('[App] Cases API response data:', data);

          const cases = data.cases || [];
          if (Array.isArray(cases)) {
            console.log(`[App] Loaded ${cases.length} cases from Neon`);
            setAlerts(cases);
            if (cases.length === 0) {
              console.log('[App] No cases in Neon, checking localStorage fallback');
              const localCases = JSON.parse(localStorage.getItem("crossregguard_cases") || "[]");
              console.log('[App] localStorage has', localCases.length, 'cases');
              setAlerts(localCases);
            }
            return;
          }
        } else {
          const errData = await res.text();
          console.error('[App] Cases API error:', res.status, errData);
        }
      } else {
        console.log('[App] Skipping Neon load - token or companyId missing');
      }

      // Fallback to localStorage
      const localCases = JSON.parse(localStorage.getItem("crossregguard_cases") || "[]");
      console.log('[App] Using localStorage fallback with', localCases.length, 'cases');
      setAlerts(localCases);
    } catch (error) {
      console.error("[App] Failed to load cases:", error);
      setAlerts([]);
    }
  }

  // ── Load TM alerts from localStorage ─────────────────────────────────────
  async function loadTmAlerts(companyId) {
    try {
      const localAlerts = JSON.parse(localStorage.getItem("crossregguard_tm_alerts") || "[]");
      setTmAlerts(localAlerts);
    } catch (error) {
      console.error("[App] Failed to load TM alerts from localStorage:", error);
      setTmAlerts([]);
    }
  }

  async function loadTeamMembers(companyId) {
    try {
      const token = localStorage.getItem('amlguard_token');
      if (!token || !companyId) return;

      // Use filter parameter like dbClient does
      const filterParam = JSON.stringify({ company_id: companyId });
      const url = `/api/query?resource=company_members&filter=${encodeURIComponent(filterParam)}`;
      console.log('[App] Loading team from:', url);

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      console.log('[App] Team fetch status:', res.status);

      if (res.ok) {
        const members = await res.json();
        console.log('[App] Loaded team members:', members);
        if (Array.isArray(members) && members.length > 0) {
          // Map database fields to expected format
          const formattedTeam = members.map(m => ({
            id: m.user_id || m.id,
            email: m.email,
            firstName: m.full_name?.split(' ')[0] || m.first_name,
            lastName: m.full_name?.split(' ').slice(1).join(' ') || m.last_name,
            first_name: m.first_name,
            last_name: m.last_name,
            role: m.role,
          }));
          console.log('[App] Formatted team:', formattedTeam);
          setTeam(formattedTeam);
        } else {
          console.warn('[App] No team members found');
        }
      } else {
        const errText = await res.text();
        console.warn('[App] Failed to load team members, status:', res.status, 'response:', errText);
      }
    } catch (error) {
      console.error('[App] Error loading team members:', error);
    }
  }

  async function loadCustomSLAThresholds(companyId) {
    try {
      const token = localStorage.getItem('amlguard_token');
      if (!token || !companyId) return;

      const filterParam = JSON.stringify({ company_id: companyId, entity_type: 'case_risk_level' });
      const url = `/api/query?resource=company_sla_thresholds&filter=${encodeURIComponent(filterParam)}`;
      console.log('[App] Loading custom SLA thresholds from:', url);

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        const thresholds = await res.json();
        console.log('[App] Loaded custom SLA thresholds:', thresholds);

        if (Array.isArray(thresholds) && thresholds.length > 0) {
          // Convert array of thresholds to object keyed by risk_level
          const slaMap = {};
          thresholds.forEach(t => {
            if (t.risk_level && t.internal_sla_days) {
              slaMap[t.risk_level] = t.internal_sla_days;
            }
          });

          // Merge with defaults so any missing levels use defaults
          setCustomSLAThresholds(prev => ({ ...prev, ...slaMap }));
          console.log('[App] Updated SLA thresholds:', { ...customSLAThresholds, ...slaMap });
        }
      } else {
        console.warn('[App] Failed to load custom SLA thresholds, status:', res.status);
      }
    } catch (error) {
      console.error('[App] Error loading custom SLA thresholds:', error);
    }
  }

  // ── Mobile responsive handler ─────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Auth boot (localStorage-based) ────────────────────────────────────────
  useEffect(() => {
    (async () => {
      // Check for Neon PostgreSQL auth tokens
      const token = localStorage.getItem('amlguard_token');
      const userStr = localStorage.getItem('amlguard_user');
      console.log('[App] Boot: token exists?', !!token, 'user exists?', !!userStr);
      if (token && userStr) {
        try {
          let user = JSON.parse(userStr);
          console.log('[App] Boot: parsed user.id:', user.id);

          // Fetch fresh user data from database to get latest permissions
          console.log('[App] Boot: fetching fresh user data from database');
          const freshUser = await fetchUserFromDatabase(token, user.id);
          if (freshUser) {
            user = freshUser;
            console.log('[App] Boot: updated user with fresh data, permission:', user.permission);
          }

          setCurrentUser(user);
          saveSession(user);
          let company = getCompany();
          console.log('[App] Boot: getCompany() returned:', company);

          if (!company) {
            console.log('[App] Boot: NO company in localStorage, fetching from database');
            company = await fetchCompanyFromDatabase(token, user.id);
          }

          if (!company) {
            console.log('[App] Boot: NO company object, creating default');
            company = {
              id: user.id,
              name: user.email ? user.email.split('@')[0] + "'s Company" : 'Your Company',
              created_at: new Date().toISOString(),
            };
            saveCompany(company);
          }

        if (!company.id) {
          console.log('[App] Boot: company exists but no id, adding fallback:', user.id);
          company.id = user.id || 'default';
          saveCompany(company);
        }

        // Always sync company to Neon on boot (in case it was deleted from Neon but still in localStorage)
        console.log('[App] Boot: Syncing company to Neon:', company.id);
        fetch('/api/query?resource=companies', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(company),
        }).then(r => {
          console.log('[App] Boot: Company sync response status:', r.status);
          if (r.ok) {
            console.log('[App] ✓ Company synced to Neon');
            return r.json();
          } else {
            return r.json().then(d => { throw new Error(d.error || 'Sync failed'); });
          }
        }).catch(e => console.error('[App] ✗ Failed to sync company to Neon:', e.message));

        setCompanyState(company);
        loadCases(company.id);
        loadTmAlerts(company.id);
        loadTeamMembers(company.id);
        loadCustomSLAThresholds(company.id);
        setPhase("app");
      } catch (e) {
        console.error('[App] Failed to parse local user:', e);
        setPhase("auth");
      }
    } else {
      console.log('[App] Boot: No token or user, checking session');
      const localSession = getSession();
      if (localSession) {
        // Use the permission from the session (set by login)
        console.log('[App] Boot: Found local session, company:', !!getCompany());
        setCurrentUser(localSession);
        let company = getCompany();
        if (!company) {
          console.log('[App] Boot: Session found but no company, creating default');
          company = {
            id: localSession.id,
            name: localSession.email ? localSession.email.split('@')[0] + "'s Company" : 'Your Company',
            created_at: new Date().toISOString(),
          };
          saveCompany(company);
          // Also save to Neon
          const token = localStorage.getItem('amlguard_token');
          if (token) {
            console.log('[App] Boot: Saving company to Neon (session path):', company);
            fetch('/api/query?resource=companies', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify(company),
            }).then(r => {
              console.log('[App] Boot: Company save response status:', r.status);
              if (r.ok) {
                console.log('[App] ✓ Company saved to Neon');
                return r.json();
              } else {
                return r.json().then(d => { throw new Error(d.error || 'Save failed'); });
              }
            }).catch(e => console.error('[App] ✗ Failed to save company to Neon:', e.message));
          }
        }
        setCompanyState(company);
        loadCases(company.id || localSession.id || 'default');
        loadTmAlerts(company.id || localSession.id || 'default');
        loadTeamMembers(company.id || localSession.id || 'default');
        loadCustomSLAThresholds(company.id || localSession.id || 'default');
        setPhase("app");
      } else {
        setPhase("auth");
      }
    }
    })();
  }, []);

  // Supabase auth state listener removed - using localStorage-based auth only
  useEffect(() => {
    // No Supabase auth state changes to listen for
    // All auth is now managed via Neon PostgreSQL and localStorage tokens
  }, []);

  // ── Auth handler ─────────────────────────────────────────────────────────
  async function handleAuth(user) {
    isAuthenticating = true;
    try {
      console.log('[App] handleAuth - user permission:', user.permission, 'email:', user.email);
      saveSession(user); setCurrentUser(user);
      console.log('[App] After setCurrentUser - currentUser.permission should be:', user.permission);
      // Get company from localStorage (set during login)
      let company = getCompany();

      // If no company exists, create a default one
      if (!company) {
        company = {
          id: user.id || 'default',
          name: user.email ? user.email.split('@')[0] + "'s Company" : 'Your Company',
          created_at: new Date().toISOString(),
        };
        console.log('[App] Created default company:', company);
        saveCompany(company);
      }

      if (company) {
        setCompanyState(company);
        await loadCases(company.id);
        await loadTmAlerts(company.id);
        await loadTeamMembers(company.id);
        await loadCustomSLAThresholds(company.id);
        setPhase("app");
      } else {
        // This shouldn't happen now, but fallback just in case
        setPhase("auth");
      }
    } catch (err) {
      console.error("[Auth] handleAuth error:", err);
      setCurrentUser(null); setPhase("auth");
    } finally {
      isAuthenticating = false;
    }
  }

  // ── Logout ───────────────────────────────────────────────────────────────
  async function handleLogout() {
    isLoggingOut = true;
    clearSession(); saveCompany(null); saveTeam([]);
    setAlerts([]); setTmAlerts([]); setOpenCase(null);
    localStorage.removeItem("crossregguard_tm_transactions");
    localStorage.removeItem("crossregguard_tm_alerts");
    setCurrentUser(null); setCompanyState(null); setTeamState([]);
    await logoutUser();
    // Redirect to homepage after logout
    window.location.href = "/crossregnexus.html";
    isLoggingOut = false;
  }

  function setCompany(co) { saveCompany(co); setCompanyState(co); }
  function setTeam(t)     { saveTeam(t);    setTeamState(t); }

  async function handleDecision(id, decision, notes, assignee) {
    const caseObj = alerts.find(a => a.id === id);
    const oldStatus = caseObj?.status;
    await auditLog({
      action: "case_status_updated",
      module: "cases",
      entityId: String(id),
      entityName: caseObj?.entity || id,
      oldValue: { status: oldStatus },
      newValue: { status: decision, notes: notes || null },
    });
    const updated = [...alerts];
    const idx = updated.findIndex(a => a.id === id);
    if (idx !== -1) {
      updated[idx] = { ...updated[idx], status: decision, notes, assignee };
      setAlerts(updated);
      localStorage.setItem("crossregguard_cases", JSON.stringify(updated));
    }
  }

  async function handleNewTMCases() {
    await loadCases(company?.id);
    await loadTmAlerts(company?.id);
    await loadCustomSLAThresholds(company?.id);
  }

  function handleTransactionClick(transaction, caseData) {
    setCryptoSearchData({
      query: transaction.id || transaction.from || transaction.to,
      network: caseData.country?.toLowerCase().replace(/\s+/g, "") || "ethereum",
      transaction,
    });
    setOpenCase(null);
    setActive("crypto");
  }

  const openAlerts = alerts.filter(a => a.status === "open").length;

  // ── Phase guards ─────────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: T.bg,
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: T.accent, fontFamily: "'DM Mono',monospace",
          fontSize: 13 }}>{t('common.loading')}</div>

      </div>
    );
  }

  // ── Check for public routes BEFORE auth phase ──────────────────────────────
  const searchParams = new URLSearchParams(window.location.search);

  // ── KYC Portal (customer onboarding without auth) ──────────────────────────
  const isKYCPortal = searchParams.get("kyc") === "1";
  const kycCompanyId = searchParams.get("company");

  if (isKYCPortal && kycCompanyId) {
    return (
      <ThemeContext.Provider value={T}>
        <div style={{ minHeight: "100vh", background: T.bg, color: T.text }}>
          <KYCOnboardingPortal companyId={kycCompanyId} />
        </div>
      </ThemeContext.Provider>
    );
  }

  // ── Accept Team Invite (no auth required) ──────────────────────────────────
  const acceptInviteToken = searchParams.get("token");
  if (acceptInviteToken) {
    return (
      <ThemeContext.Provider value={T}>
        <AcceptInvite />
      </ThemeContext.Provider>
    );
  }

  // ── Auth phase (after checking for public routes) ─────────────────────────
  if (phase === "auth") {
    return (
      <ThemeContext.Provider value={T}>
        <AuthScreen
          onAuthenticated={user => handleAuth({ ...user, permission: user.permission || "analyst" })}
          isFirstUser={getTeam().length === 0}
        />
      </ThemeContext.Provider>
    );
  }

  // ── Case detail full-screen ───────────────────────────────────────────────
  if (openCase) {
    return (
      <ThemeContext.Provider value={T}>
        <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: T.bg,
          fontFamily: "'DM Mono',monospace" }}>
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
              borderBottom: `1px solid ${T.border}`, background: T.surface }}>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{ padding: "6px 8px", background: T.accentGlow,
                  border: `1px solid ${T.accent}`, borderRadius: 6,
                  color: T.accent, cursor: "pointer", fontSize: 16,
                  fontFamily: "'DM Mono',monospace", display: "flex",
                  alignItems: "center", justifyContent: "center", width: 32, height: 32 }}>
                {mobileMenuOpen ? "✕" : "☰"}
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                <div style={{ width: 20, height: 20, background: T.accent,
                  clipPath: "polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)",
                  flexShrink: 0 }} />
                <div style={{ color: T.text, fontFamily: "'Syne',sans-serif",
                  fontWeight: 700, fontSize: 12 }}>CrossRegGuard</div>
              </div>
            </div>
          )}
          <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>
            {(!isMobile || mobileMenuOpen) && (
              <>
                <div style={{ position: isMobile ? "absolute" : "relative",
                  top: isMobile ? 52 : 0,
                  left: 0,
                  right: 0,
                  zIndex: 100,
                  maxWidth: isMobile ? "100%" : "auto",
                  background: isMobile ? T.bg : "transparent",
                  borderBottom: isMobile ? `1px solid ${T.border}` : "none",
                  maxHeight: isMobile ? `calc(100vh - 52px)` : "auto",
                  overflowY: isMobile ? "auto" : "visible" }}>
                  <Sidebar active={active} setActive={id => { setActive(id); setOpenCase(null); if (isMobile) setMobileMenuOpen(false); }}
                    company={company} currentUser={currentUser} onLogout={handleLogout}
                    openAlerts={openAlerts} lightMode={lightMode} setLightMode={setLightMode} isMobile={isMobile} />
                </div>
                {mobileMenuOpen && (
                  <div
                    onClick={() => setMobileMenuOpen(false)}
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                      zIndex: 99 }} />
                )}
              </>
            )}
            <main style={{ flex: 1, display: "flex", overflow: "auto" }}>
              <ErrorBoundary name="Case Detail">
                <CaseDetail
                  caseData={openCase}
                  onBack={() => setOpenCase(null)}
                  onDecision={handleDecision}
                  team={team}
                  currentUser={currentUser}
                  company={company}
                  onTransactionClick={handleTransactionClick}
                  onCaseUpdated={updatedCase => {
                    if (updatedCase)
                      setAlerts(prev => prev.map(a => a.id === updatedCase.id ? { ...a, ...updatedCase } : a));
                  }}
                />
              </ErrorBoundary>
            </main>
          </div>
        </div>
      </ThemeContext.Provider>
    );
  }

  // ── Main app ──────────────────────────────────────────────────────────────
  const EB = (name, child) => <ErrorBoundary name={name}>{child}</ErrorBoundary>;

  const pages = {
    dashboard:  EB("Dashboard",              <Dashboard company={company} currentUser={currentUser} team={team} alerts={alerts} tmAlerts={tmAlerts} onNavigate={handleNavigate} onOpenCase={setOpenCase} customSLAThresholds={customSLAThresholds} />),
    cases:      EB("Case Management",        <CaseManagement alerts={alerts} setAlerts={setAlerts} team={team} onOpenCase={setOpenCase} company={company} currentUser={currentUser} agingFilter={agingFilter} setAgingFilter={setAgingFilter} customSLAThresholds={customSLAThresholds} />),
    tm:         EB("Transaction Monitoring", <TransactionMonitoring onNewCases={handleNewTMCases} cases={alerts} company={company} currentUser={currentUser} />),
    sar:        EB("SAR Filing",             <SARPage currentUser={currentUser} company={company} alerts={alerts} team={team} />),
    kpi:        EB("KPI Graphs",             <KPIGraphGenerator alerts={alerts} team={team} company={company} />),
    onboarding: EB("Customer Onboarding",    <KYCReviewDashboard currentUser={currentUser} company={company} />),
    customers:  EB("Customers",              <CustomersPage currentUser={currentUser} company={company} />),
    kyc:        EB("Sanctions Screening",    <SanctionsScreeningPage currentUser={currentUser} company={company} />),
    travelrule: EB("Travel Rule",            <TravelRulePage currentUser={currentUser} company={company} />),
    edd:        EB("EDD Reviews",            <EDDPage company={company} currentUser={currentUser} alerts={alerts} tmAlerts={tmAlerts} />),
    policies:   EB("Policies & Docs",        <PoliciesPage currentUser={currentUser} team={team} company={company} />),
    crypto:     EB("Crypto Analysis",        <CryptoAnalysisPage initialSearch={cryptoSearchData} company={company} currentUser={currentUser} />),
    ai:         EB("AI Assistant",           <AIAssistant />),
    team:       EB("Team",                   <TeamPage currentUser={currentUser} setCurrentUser={setCurrentUser} team={team} setTeam={setTeam} />),
    training:   EB("Training",               <TrainingPage currentUser={currentUser} company={company} team={team} />),
    compliance: EB("Compliance Program",     <ComplianceProgramBuilder currentUser={currentUser} company={company} />),
    todo:       EB("To-Do List",             <TodoPage team={team} company={company} currentUser={currentUser} />),
    settings:   EB("Settings",              <SettingsPage company={company} currentUser={currentUser} setCompany={setCompany} onSLAUpdate={() => loadCustomSLAThresholds(company?.id)} />),
    ctr:        EB("CTR Filing",            <CTRPage currentUser={currentUser} company={company} />),
    watchlist:  EB("Internal Watchlist",   <WatchlistPage currentUser={currentUser} company={company} />),
    network:    EB("Network Graph",        <NetworkGraphPage currentUser={currentUser} company={company} />),
    chat:       EB("Team Chat",            <TeamChat currentUser={currentUser} company={company} onNavigate={handleNavigate} initialDmPerson={selectedDmPerson} />),
    audit:      EB("Audit Trail",          <AuditPage company={company} currentUser={currentUser} />),
    regreporting:     EB("Reg Reporting",          <RegulatoryReportingPage company={company} currentUser={currentUser} team={team} />),
    calendar:         EB("Compliance Calendar",    <ComplianceCalendarPage company={company} currentUser={currentUser} />),
    regcorrespondence:EB("Reg Correspondence",     <RegulatoryCorrespondencePage company={company} currentUser={currentUser} team={team} />),
    auditdocs:        EB("Audit Documentation",    <AuditDocumentation company={company} currentUser={currentUser} />),
    incident:         EB("Incident Response",      <IncidentResponsePage company={company} currentUser={currentUser} team={team} />),
    thirdpartydd:     EB("Third-Party DD",         <ThirdPartyDDPageEnhanced company={company} currentUser={currentUser} team={team} />),
    exceptions:          EB("Exception Management",   <ExceptionManagementPage company={company} currentUser={currentUser} team={team} />),
    effectivenesstesting:EB("Effectiveness Testing",  <EffectivenessTestingPage company={company} currentUser={currentUser} team={team} />),
  };

  return (
    <ThemeContext.Provider value={T}>
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: T.bg,
        color: T.text, fontFamily: "'DM Mono',monospace" }}>
        {isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
            borderBottom: `1px solid ${T.border}`, background: T.surface }}>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{ padding: "6px 8px", background: T.accentGlow,
                border: `1px solid ${T.accent}`, borderRadius: 6,
                color: T.accent, cursor: "pointer", fontSize: 16,
                fontFamily: "'DM Mono',monospace", display: "flex",
                alignItems: "center", justifyContent: "center", width: 32, height: 32 }}>
              {mobileMenuOpen ? "✕" : "☰"}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
              <div style={{ width: 20, height: 20, background: T.accent,
                clipPath: "polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)",
                flexShrink: 0 }} />
              <div style={{ color: T.text, fontFamily: "'Syne',sans-serif",
                fontWeight: 700, fontSize: 12 }}>CrossRegGuard</div>
            </div>
          </div>
        )}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {(!isMobile || mobileMenuOpen) && (
            <div style={{ position: isMobile ? "absolute" : "relative",
              top: isMobile ? 52 : 0,
              left: 0,
              right: 0,
              zIndex: 100,
              maxWidth: isMobile ? "100%" : "auto",
              background: isMobile ? T.bg : "transparent",
              borderBottom: isMobile ? `1px solid ${T.border}` : "none",
              maxHeight: isMobile ? `calc(100vh - 52px)` : "auto",
              overflowY: isMobile ? "auto" : "visible" }}>
              <Sidebar active={active} setActive={id => {
                setActive(id);
                if (isMobile) setMobileMenuOpen(false);
              }} company={company}
                currentUser={currentUser} onLogout={handleLogout} openAlerts={openAlerts}
                lightMode={lightMode} setLightMode={setLightMode} isMobile={isMobile} />
            </div>
          )}
          {mobileMenuOpen && (
            <div
              onClick={() => setMobileMenuOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                zIndex: 99, top: 52 }} />
          )}
          <main style={{ flex: 1, padding: isMobile ? "20px 16px" : "32px 36px",
            overflowY: "auto", display: "flex", flexDirection: "column",
            width: "100%" }}>
            <div style={{ flex: 1 }}>{pages[active]}</div>
            <div style={{
              marginTop: 48, padding: "14px 0 4px",
              borderTop: `1px solid ${T.border}`,
              color: T.textDim, fontSize: isMobile ? 9 : 10,
              fontFamily: "'DM Mono',monospace",
              lineHeight: 1.6, letterSpacing: "0.02em",
            }}>
              <strong style={{ color: T.textMid }}>Disclaimer:</strong> CrossRegGuard is a compliance management tool designed to assist your organization in organizing and tracking compliance-related activities. It does not constitute legal or regulatory advice. Your organization remains solely responsible for ensuring full compliance with all applicable laws, regulations, and regulatory obligations — including but not limited to BSA, AML, OFAC, FinCEN, FATF, and any jurisdiction-specific requirements. Always consult qualified legal counsel and your regulatory authority for compliance determinations.
            </div>
          </main>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}
